import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Grid,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Phone,
  Email,
  Lock,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '+7',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validatePhone = (phone) => {
    const phoneRegex = /^\+7\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Для телефона - только цифры, +7 всегда в начале
    if (name === 'phone') {
      // Удаляем все символы кроме цифр и +
      value = value.replace(/[^\d+]/g, '');
      
      // Если начинается не с +7, добавляем +7
      if (!value.startsWith('+7')) {
        // Удаляем все + и цифры, оставляем только цифры
        const digits = value.replace(/\D/g, '');
        value = '+7' + digits;
      } else {
        // Если уже есть +7, оставляем только цифры после +7
        const digits = value.replace(/\D/g, '').replace(/^7/, '');
        value = '+7' + digits;
      }
      
      // Ограничиваем длину до +7 + 10 цифр
      value = value.slice(0, 12);
    }
    
    setFormData({
      ...formData,
      [name]: value,
    });
    setError('');
    setErrors({ ...errors, [name]: '' });
  };

  const validateForm = () => {
    const newErrors = {};

    // Валидация имени
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Имя обязательно';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'Имя должно содержать минимум 2 символа';
    }

    // Валидация фамилии
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Фамилия обязательна';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Фамилия должна содержать минимум 2 символа';
    }

    // Валидация email
    if (!formData.email.trim()) {
      newErrors.email = 'Email обязателен';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Введите корректный email';
    }

    // Валидация телефона
    if (!formData.phone || formData.phone === '+7') {
      newErrors.phone = 'Введите номер телефона';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Телефон должен быть в формате +79001234567';
    }

    // Валидация пароля
    if (!formData.password) {
      newErrors.password = 'Пароль обязателен';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Пароль должен содержать минимум 8 символов';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Пароль должен содержать заглавные и строчные буквы, а также цифры';
    }

    // Валидация подтверждения пароля
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Подтвердите пароль';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setError('Пожалуйста, исправьте ошибки в форме');
      return;
    }

    setLoading(true);

    try {
      await register({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone,
        password: formData.password,
      });
      navigate('/pending');
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: isDarkMode ? '#0d1117' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 4,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: isDarkMode ? `
            radial-gradient(circle at 30% 70%, rgba(163, 190, 140, 0.12) 0%, transparent 40%),
            radial-gradient(circle at 70% 30%, rgba(180, 142, 173, 0.12) 0%, transparent 40%),
            radial-gradient(circle at 50% 50%, rgba(136, 192, 208, 0.08) 0%, transparent 30%)
          ` : `
            radial-gradient(circle at 30% 70%, rgba(163, 190, 140, 0.25) 0%, transparent 40%),
            radial-gradient(circle at 70% 30%, rgba(180, 142, 173, 0.2) 0%, transparent 40%),
            radial-gradient(circle at 50% 50%, rgba(136, 192, 208, 0.15) 0%, transparent 30%)
          `,
          animation: 'aurora 25s ease-in-out infinite',
          '@keyframes aurora': {
            '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
            '33%': { transform: 'translate(3%, -3%) rotate(2deg)' },
            '66%': { transform: 'translate(-3%, 3%) rotate(-2deg)' },
          },
        },
      }}
    >
      {/* Floating orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          right: '10%',
          width: 350,
          height: 350,
          borderRadius: '50%',
          background: isDarkMode 
            ? 'radial-gradient(circle, rgba(163, 190, 140, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(163, 190, 140, 0.2) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'float 12s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) scale(1)' },
            '50%': { transform: 'translateY(-40px) scale(1.05)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: isDarkMode 
            ? 'radial-gradient(circle, rgba(180, 142, 173, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(180, 142, 173, 0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'float 15s ease-in-out infinite 3s',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          sx={{
            background: isDarkMode 
              ? 'rgba(22, 27, 34, 0.7)'
              : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
            borderRadius: 4,
            boxShadow: isDarkMode ? `
              0 32px 64px rgba(0, 0, 0, 0.4),
              0 0 0 1px rgba(255, 255, 255, 0.03) inset,
              0 0 80px rgba(163, 190, 140, 0.1)
            ` : `
              0 32px 64px rgba(0, 0, 0, 0.12),
              0 0 0 1px rgba(0, 0, 0, 0.02) inset,
              0 0 80px rgba(163, 190, 140, 0.08)
            `,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: 'linear-gradient(90deg, transparent, #a3be8c, #88c0d0, #b48ead, transparent)',
              borderRadius: '4px 4px 0 0',
            },
          }}
        >
          <CardContent sx={{ p: 5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4,
              }}
            >
              {/* Animated Logo */}
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 50%, #b48ead 100%)',
                  backgroundSize: '200% 200%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  boxShadow: '0 16px 40px rgba(163, 190, 140, 0.3)',
                  animation: 'gradient-shift 4s ease infinite, float 6s ease-in-out infinite',
                  '@keyframes gradient-shift': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0d1117' }}>
                  N
                </Typography>
              </Box>
              
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 50%, #b48ead 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                  mb: 1,
                }}
              >
                Регистрация
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Создайте аккаунт менеджера в Nord CRM
              </Typography>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  background: 'rgba(191, 97, 106, 0.15)',
                  border: '1px solid rgba(191, 97, 106, 0.3)',
                  borderRadius: 2,
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Имя"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: '#88c0d0' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Фамилия"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: '#88c0d0' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#ebcb8b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Номер телефона"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+79001234567"
                    error={!!errors.phone}
                    helperText={errors.phone}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9+]*',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: '#a3be8c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Пароль"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    error={!!errors.password}
                    helperText={errors.password || 'Минимум 8 символов, заглавные и строчные буквы, цифры'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#b48ead' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'text.secondary' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Подтвердите пароль"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#b48ead' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 4,
                  mb: 2,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
                  boxShadow: '0 8px 32px rgba(163, 190, 140, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #88c0d0 0%, #b48ead 100%)',
                    boxShadow: '0 12px 40px rgba(163, 190, 140, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: 'rgba(163, 190, 140, 0.3)',
                  },
                }}
              >
                {loading ? 'Регистрация...' : 'Зарегистрироваться'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Уже есть аккаунт?{' '}
                  <Link
                    component={RouterLink}
                    to="/login"
                    sx={{ 
                      fontWeight: 600, 
                      textDecoration: 'none',
                      color: '#88c0d0',
                      '&:hover': {
                        color: '#b48ead',
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    Войти
                  </Link>
                </Typography>
              </Box>
            </form>

            {/* Info note */}
            <Box 
              sx={{ 
                mt: 4, 
                p: 2.5, 
                background: isDarkMode ? 'rgba(235, 203, 139, 0.1)' : 'rgba(235, 203, 139, 0.15)',
                border: '1px solid rgba(235, 203, 139, 0.2)',
                borderRadius: 2,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#ebcb8b', 
                  fontWeight: 600,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                ℹ️ Информация
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                После регистрации ваш аккаунт будет ожидать одобрения администратором.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 4, 
            color: 'text.secondary',
          }}
        >
          © 2024 Nord CRM. Управление продажами нового поколения.
        </Typography>
      </Container>
    </Box>
  );
};

export default Register;
